import React, { useState } from "react";
import { Heading2, RadioButton, SecondaryButton } from "widgets";
import { OFFERING_TYPE, PRODUCT, SERVICE } from "constants/config";
import CloseIcon from "@mui/icons-material/Close";
import AddProductOffering from "./AddProductOffering";
import AddServiceOffering from "./AddServiceOffering";

import styles from "./AddPackageFeatureOffering.module.scss";

const DisplayDetails = ({ title, value }) => (
  <div className={styles.displayDetails}>
    <span className={styles.title}>{title}</span>
    <span className={styles.value}>{value || "N/A"}</span>
  </div>
);

const isServiceFeatureOfferingValid = (offering) => {
  const { quantity, duration, durationType } = offering;

  return (
    (quantity && quantity.trim() !== "") ||
    (duration && duration.trim() !== "" && durationType && Object.keys(durationType).length > 0)
  );
};

const isObjectValid = (obj, offeringType) => {
  if (offeringType === SERVICE) {
    return isServiceFeatureOfferingValid(obj);
  }
  return Object.values(obj).every((value) =>
    typeof value === "object" && value !== null
      ? Object.keys(value).length > 0
      : value !== ""
  );
};

const handleAddFeature = (offeringType, currentOfferings, setOfferings) => {
  const newOffering =
    offeringType === PRODUCT
      ? { productOffering: { label: "" }, description: "", quantity: "" }
      : {
          feature: {},
          offering: {},
          description: "",
          quantity: "",
          duration: "",
          durationType: {},
          usageDate: {},
        };
  setOfferings([...currentOfferings, newOffering]);
};

const handleRemoveFeature = (index, currentOfferings, setOfferings) => {
  const updatedOfferings = currentOfferings.filter((_, i) => i !== index);
  setOfferings(updatedOfferings);
};

const AddPackageFeatureOffering = ({
  newProductFeatureOffering,
  newServiceFeatureOffering,
  handleChangeProductFeatureOffering,
  handleChangeServiceFeatureOffering,
  handleAddFeatureOffering,
  productFeatureOfferingErrors,
  serviceFeatureOfferingErrors,
}) => {
  const [offeringType, setOfferingType] = useState("");

  const currentOfferings =
    offeringType === PRODUCT
      ? newProductFeatureOffering
      : newServiceFeatureOffering;

  const handleUpdateOfferings =
    offeringType === PRODUCT
      ? handleChangeProductFeatureOffering
      : handleChangeServiceFeatureOffering;

  const isAddFeaturesDisabled =
    currentOfferings.length === 0 ||
    !isObjectValid(currentOfferings[currentOfferings.length - 1], offeringType);

  return (
    <div className={styles.mainContainer}>
      <Heading2 heading="Add Package Feature Offering" />
      <div className={styles.offeringInnerContainer}>
        <RadioButton
          value={offeringType}
          options={OFFERING_TYPE}
          onChange={(event) => setOfferingType(event.target.value)}
        />
        {currentOfferings.length > 0 && (
          offeringType === PRODUCT ? (
            <AddProductOffering
              key={currentOfferings.length - 1}
              offering={currentOfferings[currentOfferings.length - 1]}
              handleProductFeatureOffering={(updated) => {
                const updatedOfferings = [...currentOfferings];
                updatedOfferings[currentOfferings.length - 1] = updated;
                handleUpdateOfferings(updatedOfferings);
              }}
              errors={
                productFeatureOfferingErrors
                  ? productFeatureOfferingErrors[currentOfferings.length - 1]
                  : []
              }
            />
          ) : (
            <AddServiceOffering
              key={currentOfferings.length - 1}
              offering={currentOfferings[currentOfferings.length - 1]}
              handleServiceFeatureOffering={(updated) => {
                const updatedOfferings = [...currentOfferings];
                updatedOfferings[currentOfferings.length - 1] = updated;
                handleUpdateOfferings(updatedOfferings);
              }}
              errors={
                serviceFeatureOfferingErrors
                  ? serviceFeatureOfferingErrors[currentOfferings.length - 1]
                  : []
              }
            />
          )
        )}
        <div className={styles.addFeaturesButton}>
          <SecondaryButton
            onClick={() =>
              handleAddFeature(offeringType, currentOfferings, handleUpdateOfferings)
            }
            disabled={isAddFeaturesDisabled}
          >
            Add New Features
          </SecondaryButton>
        </div>
        <div className={styles.displayAddedFeatures}>
          <div className={styles.dashedLine} />
          {currentOfferings
            .slice()
            .reverse()
            .map((offering, index) => (
              <div key={index} className={styles.displayFeatures}>
                {index !== 0 && (
                  <CloseIcon
                    className={styles.crossIcon}
                    onClick={() =>
                      handleRemoveFeature(
                        currentOfferings.length - 1 - index,
                        currentOfferings,
                        handleUpdateOfferings
                      )
                    }
                  />
                )}
                {offeringType === PRODUCT ? (
                  <>
                    <DisplayDetails title="Product Offering" value={offering.productOffering.label} />
                    <DisplayDetails title="Quantity" value={offering.quantity} />
                  </>
                ) : (
                  <>
                    <div className={styles.flexContainer}>
                      <DisplayDetails title="Feature" value={offering.feature.value} />
                      <DisplayDetails title="Offering" value={offering.offering.value} />
                    </div>
                    <div className={styles.flexContainer}>
                      <DisplayDetails title="Usage Date" value={offering.usageDate.value} />
                      <DisplayDetails title="Quantity" value={offering.quantity} />
                    </div>
                    <div className={styles.flexContainer}>
                      <DisplayDetails title="Duration" value={offering.duration} />
                      <DisplayDetails title="Duration Type" value={offering.durationType.value} />
                    </div>
                  </>
                )}
              </div>
            ))}
          <div className={styles.addFeatureOfferingButton}>
            <SecondaryButton onClick={handleAddFeatureOffering}>
              Add Feature-Offering
            </SecondaryButton>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddPackageFeatureOffering;
